import React, { useState, useRef } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import SelectField from "../../components/ui/SelectField";
import { toast } from "react-toastify";
import { CheckCircle, AlertTriangle, XCircle, Loader2, Search, Hammer } from "lucide-react";

const CONCURRENCY = 3;

async function runWithConcurrency(items, limit, worker) {
  let index = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (index < items.length) {
      const current = index++;
      await worker(items[current], current);
    }
  });
  await Promise.all(runners);
}

const STATUS_LABEL = {
  pending: "Pendiente",
  checking: "Verificando...",
  done: "Analizada",
  building: "Construyendo...",
  error: "Error",
};

const CaratulasTodasEspecies = ({ especies, examenes, equipos, reactivos }) => {
  const [selectedExamen, setSelectedExamen] = useState("");
  const [selectedEquipo, setSelectedEquipo] = useState("");
  const [selectedReactivo, setSelectedReactivo] = useState("");

  const [rows, setRows] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [buildingAll, setBuildingAll] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [buildingCodes, setBuildingCodes] = useState(() => new Set());

  const rowsRef = useRef([]);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/util";

  const isSelectionComplete = selectedExamen && selectedEquipo && selectedReactivo;
  const isBusy = analyzing || buildingAll || buildingCodes.size > 0;

  const verifyOne = async (codigoEspecie) => {
    const { data } = await axios.get(`${API_URL}/caratulas_especie/verify`, {
      params: { especie: codigoEspecie, examen: selectedExamen, equipo: selectedEquipo, reactivo: selectedReactivo },
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  };

  const setRow = (codigoEspecie, patch) => {
    setRows((prev) => {
      const next = prev.map((r) => (r.codigo === codigoEspecie ? { ...r, ...patch } : r));
      rowsRef.current = next;
      return next;
    });
  };

  const handleAnalyzeAll = async () => {
    if (!isSelectionComplete) {
      toast.error("Debes seleccionar examen, equipo y reactivo");
      return;
    }
    if (especies.length === 0) {
      toast.error("No hay especies cargadas");
      return;
    }

    setAnalyzing(true);
    const initialRows = especies.map((es) => ({ codigo: es.codigo, detalle: es.detalle, status: "pending" }));
    rowsRef.current = initialRows;
    setRows(initialRows);

    await runWithConcurrency(especies, CONCURRENCY, async (es) => {
      setRow(es.codigo, { status: "checking" });
      try {
        const data = await verifyOne(es.codigo);
        setRow(es.codigo, { status: "done", ...data, errorMsg: undefined });
      } catch (error) {
        setRow(es.codigo, { status: "error", errorMsg: error.response?.data?.error || "Error al verificar" });
      }
    });

    setAnalyzing(false);
  };

  const buildOne = async (codigoEspecie) => {
    setBuildingCodes((prev) => new Set(prev).add(codigoEspecie));
    setRow(codigoEspecie, { status: "building" });
    try {
      await axios.post(
        `${API_URL}/caratulas_especie/create`,
        { especie: codigoEspecie, examen: selectedExamen, equipo: selectedEquipo, reactivo: selectedReactivo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await verifyOne(codigoEspecie);
      setRow(codigoEspecie, { status: "done", ...data, errorMsg: undefined });
    } catch (error) {
      setRow(codigoEspecie, { status: "error", errorMsg: error.response?.data?.error || "Error al construir" });
    } finally {
      setBuildingCodes((prev) => {
        const next = new Set(prev);
        next.delete(codigoEspecie);
        return next;
      });
    }
  };

  const handleBuildRow = async (codigoEspecie) => {
    await buildOne(codigoEspecie);
  };

  const incompleteRows = rows.filter((r) => r.status === "done" && r.completa === false);

  const handleConfirmBuildIncomplete = async () => {
    setShowConfirm(false);
    setBuildingAll(true);
    const targets = incompleteRows;
    await runWithConcurrency(targets, CONCURRENCY, (r) => buildOne(r.codigo));
    setBuildingAll(false);
    const failedCount = targets.filter((r) => {
      const current = rowsRef.current.find((row) => row.codigo === r.codigo);
      return current?.status === "error";
    }).length;
    if (failedCount > 0) {
      toast.warning(`Construcción masiva finalizada con ${failedCount} error(es)`);
    } else {
      toast.success("Construcción masiva finalizada");
    }
  };

  const analyzedCount = rows.filter((r) => r.status === "done" || r.status === "error").length;
  const completeCount = rows.filter((r) => r.status === "done" && r.completa).length;
  const errorCount = rows.filter((r) => r.status === "error").length;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600">
          Analiza y construye la carátula de un examen para todas las especies a la vez, sin bloquear el sistema.
        </p>
      </div>

      <div className="bg-brand-50 border border-brand-100 rounded-xl p-4">
        <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider mb-3">
          Analizar todas las especies
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <SelectField name="examen_all" value={selectedExamen} onChange={(e) => setSelectedExamen(e.target.value)}
            disabled={isBusy} placeholder="Selecciona examen" options={examenes} valueKey="examen_cod"
            displayFn={(ex) => `${ex.examen_cod} - ${ex.nombre}`} />
          <SelectField name="equipo_all" value={selectedEquipo} onChange={(e) => setSelectedEquipo(e.target.value)}
            disabled={isBusy} placeholder="Selecciona equipo" options={equipos} valueKey="equipo_cod"
            displayFn={(eq) => `${eq.equipo_cod} - ${eq.equipo_desc}`} />
          <SelectField name="reactivo_all" value={selectedReactivo} onChange={(e) => setSelectedReactivo(e.target.value)}
            disabled={isBusy} placeholder="Selecciona reactivo" options={reactivos} valueKey="reactivo_cod"
            displayFn={(re) => `${re.reactivo_cod} - ${re.reactivo_deta}`} />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="outline" onClick={handleAnalyzeAll} disabled={isBusy || !isSelectionComplete} className="gap-2">
            <Search className="h-4 w-4" /> {analyzing ? "Analizando..." : "Analizar todas las especies"}
          </Button>
          {rows.length > 0 && (
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={isBusy || incompleteRows.length === 0}
              className="gap-2"
            >
              <Hammer className="h-4 w-4" />
              {buildingAll ? "Construyendo..." : `Construir incompletas (${incompleteRows.length})`}
            </Button>
          )}
        </div>
      </div>

      {rows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">
              {analyzedCount}/{rows.length} especies analizadas
            </p>
            <p className="text-xs text-gray-500">
              {completeCount} completas · {incompleteRows.length} incompletas · {errorCount} errores
            </p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
            <div
              className="bg-brand-500 h-2 rounded-full transition-all"
              style={{ width: `${rows.length ? (analyzedCount / rows.length) * 100 : 0}%` }}
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="py-2 pr-2">Especie</th>
                  <th className="py-2 pr-2">Analitos</th>
                  <th className="py-2 pr-2">Razas</th>
                  <th className="py-2 pr-2">Esperado</th>
                  <th className="py-2 pr-2">Actual</th>
                  <th className="py-2 pr-2">Estado</th>
                  <th className="py-2 pr-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.codigo} className="border-t border-gray-100">
                    <td className="py-2 pr-2 font-medium text-gray-800">{r.detalle}</td>
                    <td className="py-2 pr-2">{r.totalAnalitos ?? "-"}</td>
                    <td className="py-2 pr-2">{r.totalRazas ?? "-"}</td>
                    <td className="py-2 pr-2">{r.totalEsperado ?? "-"}</td>
                    <td className="py-2 pr-2">{r.totalActual ?? "-"}</td>
                    <td className="py-2 pr-2">
                      <span className="inline-flex items-center gap-1">
                        {r.status === "checking" || r.status === "building" ? (
                          <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
                        ) : r.status === "done" && r.completa ? (
                          <CheckCircle className="h-4 w-4 text-emerald-600" />
                        ) : r.status === "done" && !r.completa ? (
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                        ) : r.status === "error" ? (
                          <XCircle className="h-4 w-4 text-red-600" />
                        ) : null}
                        <span className="text-xs text-gray-600" title={r.errorMsg}>
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </span>
                    </td>
                    <td className="py-2 pr-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isBusy || r.status === "checking" || r.status === "building"}
                        onClick={() => handleBuildRow(r.codigo)}
                      >
                        Construir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Confirmar construcción masiva</h2>
            <p className="text-sm text-gray-600 mb-3">
              Se reconstruirá la carátula del examen seleccionado para {incompleteRows.length} especie(s),
              reemplazando los registros existentes de equipo/reactivo en esas especies:
            </p>
            <ul className="text-sm text-gray-700 max-h-40 overflow-y-auto list-disc list-inside mb-4">
              {incompleteRows.map((r) => (
                <li key={r.codigo}>{r.detalle}</li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancelar</Button>
              <Button onClick={handleConfirmBuildIncomplete}>Confirmar y construir</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaratulasTodasEspecies;
